import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { environment } from 'src/environments/environment';
import { Installation } from '../models/service-order.model';

/**
 * Mapa estático (Google Static Maps) no painel. Cada exibição consome uma chamada da API, por
 * isso fica desligado; ligue aqui para testar. Se virar recurso de produto, o lugar definitivo
 * é uma flag de environment.
 **/
const STATIC_MAP_ENABLED = false;

/**
 * Painel lateral com o contexto da instalação escolhida para a OS: responde "é este poste?"
 * com endereço, equipamento e gateway. Os dados vêm da mesma consulta do autocomplete, sem
 * chamada extra. O mapa estático é opcional (constante STATIC_MAP_ENABLED); desligado, mostra
 * coordenadas e link para o Google Maps.
 */
@Component({
  selector: 'app-service-order-installation-panel',
  templateUrl: './service-order-installation-panel.component.html',
  styleUrls: ['./service-order-installation-panel.component.less'],
  imports: [CommonModule, TranslateModule],
})
export class ServiceOrderInstallationPanelComponent {
  /** null = nada escolhido ainda: painel mostra a instrução **/
  @Input() installation: Installation | null = null;

  /** Buscando os dados da instalação (Ficha da OS): mostra o estado de carregamento **/
  @Input() loading: boolean = false;

  /**
   * Referência que o painel deveria exibir (Ficha da OS). Com referência e sem instalação, o
   * vazio vira "não encontrada no sistema"; sem referência (criação), vira "escolha uma".
   **/
  @Input() reference: string | null = null;

  public get notFound(): boolean {
    return !this.loading && !this.installation && !!this.reference;
  }

  /** A OS guardou uma plaqueta que depois foi renomeada: mostra as duas **/
  public get referenceChanged(): boolean {
    const current = this.installation?.reference?.trim().toLowerCase();
    const recorded = this.reference?.trim().toLowerCase();

    return !!current && !!recorded && current !== recorded;
  }

  public readonly staticMapEnabled: boolean = STATIC_MAP_ENABLED;

  public get hasCoordinates(): boolean {
    return this.installation?.latitude != null && this.installation?.longitude != null;
  }

  public get coordinates(): string | null {
    if (!this.hasCoordinates) {
      return null;
    }

    return `${this.installation!.latitude!.toFixed(5)}, ${this.installation!.longitude!.toFixed(5)}`;
  }

  /** Endereço em duas linhas: "Rua X, 120" e "Bairro · Cidade/UF" **/
  public get addressLine(): string | null {
    return this.installation?.details?.street ?? null;
  }

  public get localityLine(): string | null {
    const details = this.installation?.details;
    const cityState = [details?.city, details?.state].filter(Boolean).join('/');

    return [details?.district, cityState].filter(Boolean).join(' · ') || null;
  }

  public get hasDevice(): boolean {
    return !!(this.installation?.serialNumber || this.installation?.deviceMac);
  }

  public get mapsLink(): string | null {
    if (!this.hasCoordinates) {
      return null;
    }

    return `https://www.google.com/maps?q=${this.installation!.latitude},${this.installation!.longitude}`;
  }

  /** Reaproveita a chave já configurada para o Google Maps JS (environment.googleMapsApiKeyUrl) **/
  public get staticMapUrl(): string | null {
    if (!this.staticMapEnabled || !this.hasCoordinates) {
      return null;
    }

    const key = /[?&]key=([^&]+)/.exec(environment.googleMapsApiKeyUrl ?? '')?.[1];

    if (!key) {
      return null;
    }

    const point = `${this.installation!.latitude},${this.installation!.longitude}`;

    return `https://maps.googleapis.com/maps/api/staticmap?center=${point}&zoom=16&size=440x220&scale=1&markers=color:0x1d6369%7C${point}&key=${key}`;
  }
}
